import { FormEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import { useReturnsMutations } from "../../data/useReturns";
import { ReturnDetail } from "../../models/returns/IReturnsApi";

/** Suggestions only: any courier can be typed. These are the ones the tracking link knows. */
const COURIERS = [
  "DPD",
  "Royal Mail",
  "Parcelforce",
  "Evri",
  "UPS",
  "DHL",
  "FedEx",
  "Proovia",
];

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

interface ReturnTrackingFormProps {
  detail: ReturnDetail;
  /** After a save: the page opens the message panel with the own_tracking template. */
  onSaved: () => void;
}

/**
 * "Coming back another way?": the real return courier and tracking number when the buyer isn't
 * using eBay's label. Saved in Wilma only; eBay isn't told. The message to the buyer is a
 * separate step and is only sent on its own Confirm.
 */
export default function ReturnTrackingForm({
  detail,
  onSaved,
}: ReturnTrackingFormProps) {
  const { t } = useTranslation("returns");
  const { tracking } = useReturnsMutations();
  const own = detail.ownTracking;
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState(own?.carrier ?? "");
  const [number, setNumber] = useState(own?.tracking ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const start = () => {
    setCarrier(own?.carrier ?? "");
    setNumber(own?.tracking ?? "");
    setError(null);
    setSaved(false);
    setOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!carrier.trim() || !number.trim()) return;
    setError(null);
    try {
      await tracking.mutateAsync({
        id: detail.id,
        body: { carrier: carrier.trim(), tracking: number.trim() },
      });
      setOpen(false);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(errorText(err, t("Couldn't save the tracking.")));
    }
  };

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
        <span className="text-gray-500">
          {own ? t("Tracking changed?") : t("Coming back another way?")}
        </span>
        <button
          type="button"
          onClick={start}
          className="font-semibold text-primary-700 hover:underline"
        >
          {own ? t("Update return tracking") : t("Add return tracking")}
        </button>
        {saved && (
          <span className="text-green-700">
            {t("Saved in Wilma. eBay isn't told.")}
          </span>
        )}
      </div>
    );
  }

  const busy = tracking.isLoading;
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]"
    >
      <span className="font-semibold text-gray-900">
        {t("Coming back another way?")}
      </span>
      <span className="text-gray-500">
        {t(
          "Add the courier and tracking the buyer is really using. It's saved in Wilma only — eBay isn't told. You can message the buyer next.",
        )}
      </span>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-0.5 text-gray-700">
          {t("Courier")}
          <input
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            list="return-couriers"
            required
            maxLength={64}
            disabled={busy}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
          <datalist id="return-couriers">
            {COURIERS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-0.5 text-gray-700">
          {t("Tracking number")}
          <input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            required
            maxLength={128}
            disabled={busy}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 font-mono text-[13px] text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
        </label>
      </div>
      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={busy || !carrier.trim() || !number.trim()}
          className="min-h-[36px] rounded-lg bg-primary-600 px-3.5 text-[13px] font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? t("Saving…") : t("Save tracking")}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={busy}
          className="min-h-[36px] rounded-lg border border-gray-300 bg-white px-3 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}
